import {Button, Col, DatePicker, Form, Input, Row, Select, Spin, Table, Typography} from 'antd'
import moment from 'moment'
import {CloudDownloadOutlined, SearchOutlined, EditOutlined} from '@ant-design/icons'
import store from 'store'
import {CSVLink} from 'react-csv'
import React, {useCallback, useEffect, useImperativeHandle, useMemo, useState} from 'react'
import AxiosUtil from '../../../utils/AxiosUtil'
import type {ColumnsType, TableProps} from 'antd/es/table'

type IProps = {
  onRef?: any
  screenType?: 0 | 1 // 0: 分析报告; 1: 打分
  onOperate?: (curItem: any) => void
  onUserListChange?: (list: any[]) => void
}
type ITableDataItem = {
  id: number
  symbol: string
  securityName: string
  userId: string
  userName: string
  taskStatus: number
  taskDay: string
  digitalSign: number
  sfcIndustry1Code: string
  sfcIndustry1Name: string
  finishDay: string
  taskType: number
}
type IMapData = {industryMap: {[x: string]: string}; taskMap: {[x: string]: string}; taskTypeMap: {[x: string]: string}}

const formItemStyle: React.CSSProperties = {marginBottom: 20}
const defaultIndustryMap = {
  A: '农、林、牧、渔业',
  B: '采矿业',
  C: '制造业',
  D: '电力、热力、燃气及水生产和供应业',
  E: '建筑业',
  F: '批发和零售业',
  G: '交通运输、仓储和邮政业',
  H: '住宿和餐饮业',
  I: '信息传输、软件和信息技术服务业',
  J: '金融业',
  K: '房地产业',
  L: '租赁和商务服务业',
  M: '科学研究和技术服务业',
  N: '水利、环境和公共设施管理业',
  O: '居民服务、修理和其他服务业',
  P: '教育',
  Q: '卫生和社会工作',
  R: '文化、体育和娱乐业',
  S: '综合',
}
const defaultTaskMap: {[x: number]: string} = {0: '未分配', 1: '已分配', 2: '已完成', 3: '无需处理'}
const defaultTaskTypeMap: {[x: number]: string} = {0: '分析报告', 1: '企业评价模型', 2: '数字产业化评价模型'}
const defaultMapData: IMapData = {industryMap: defaultIndustryMap, taskMap: defaultTaskMap, taskTypeMap: defaultTaskTypeMap}
const digitalSign = [
  {label: '有标识', value: 1},
  {label: '无标识', value: 0},
]
const csvName = 'tasks_data.csv'
const csvHeader = [
  {label: '任务ID', key: 'id'},
  {label: '股票代码', key: 'symbol'},
  {label: '股票名称', key: 'securityName'},
  {label: '标识', key: 'digitalSign'},
  {label: '行业', key: 'sfcIndustry1Code'},
  {label: '行业名称', key: 'sfcIndustry1Name'},
  {label: '任务类型', key: 'taskType'},
  {label: '任务日期', key: 'taskDay'},
  {label: '任务人名称', key: 'userName'},
  {label: '完成日期', key: 'finishDay'},
  {label: '任务状态', key: 'taskStatus'},
]
const csvLinkId = 'csvLinkId'
const initTableData: {total: number; list: ITableDataItem[]} = {total: 0, list: []}
const orderMap: {[x: string]: number} = {
  taskDayascend: 1,
  taskDaydescend: 0,
  iddescend: 11,
  idascend: 12,
  symboldescend: 21,
  symbolascend: 22,
  securityNameascend: 31,
  securityNamedescend: 32,
  taskTypedescend: 41,
  taskTypeascend: 42,
  userNamedescend: 51,
  userNameascend: 52,
  finishDaydescend: 61,
  finishDayascend: 62,
  taskStatusdescend: 71,
  taskStatusascend: 72,
  sfcIndustry1Codedescend: 81,
  sfcIndustry1Codeascend: 82,
  sfcIndustry1Namedescend: 91,
  sfcIndustry1Nameascend: 92,
}
const pageSize = 20

export default function MyDivTable({onUserListChange, onRef, onOperate, screenType}: IProps) {
  const loginInfo = store.get('login_info')
  const defaultUserList = useMemo(() => [{label: loginInfo.user_name, value: loginInfo.user_id}], [loginInfo.user_id, loginInfo.user_name])
  const [form] = Form.useForm() // 筛选组件中的 form
  const [userList, setUserList] = useState(defaultUserList)
  const [pageIndex, setPageIndex] = useState(1) // 当前页码
  const [loading, setLoading] = useState(false) // 是否加载中
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]) // table 中选中项的 id
  const [tableData, setTableData] = useState(initTableData) // table 数据
  const [order, setOrder] = useState(0) // 按什么排序
  const [csvData, setCsvData] = useState<ITableDataItem[]>([]) // 下载的 CSV 数据
  const [mapData, setMapData] = useState(defaultMapData) // 筛选框的数据来源

  /** 任务列表 table 的 columns */
  const columns: ColumnsType<ITableDataItem> = [
    {title: '任务ID', dataIndex: 'id', align: 'center' as 'center', sorter: true},
    {title: '股票代码', dataIndex: 'symbol', align: 'center' as 'center', sorter: true},
    {title: '股票名称', dataIndex: 'securityName', align: 'center' as 'center', sorter: true},
    {title: '标识', dataIndex: 'digitalSign', align: 'center' as 'center', render: (digitalSign) => (digitalSign ? '有' : '无')},
    {title: '行业', dataIndex: 'sfcIndustry1Code', align: 'center' as 'center', sorter: true},
    {title: '行业名称', dataIndex: 'sfcIndustry1Name', align: 'center' as 'center', sorter: true},
    {title: '任务类型', dataIndex: 'taskType', align: 'center' as 'center', sorter: true, render: (type) => mapData.taskTypeMap[type]},
    {
      title: '任务日期',
      dataIndex: 'taskDay',
      align: 'center' as 'center',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (date) => (date ? moment(date).format('YYYY-MM-DD') : ''),
    },
    {title: '任务人名称', dataIndex: 'userName', align: 'center' as 'center', sorter: true},
    {
      title: '完成日期',
      dataIndex: 'finishDay',
      align: 'center' as 'center',
      sorter: true,
      render: (date) => (date ? moment(date).format('YYYY-MM-DD') : ''),
    },
    {title: '任务状态', dataIndex: 'taskStatus', align: 'center' as 'center', sorter: true, render: (status) => mapData.taskMap[status]},
  ]
  /** Table 的操作 Col */
  const operateCol = {
    title: '操作',
    dataIndex: 'taskStatus',
    align: 'center' as 'center',
    render: (status: string, row: any) => (
      <Typography.Link onClick={() => onOperate && onOperate({...row})}>
        {['1', 1].includes(status) ? (
          <>
            <EditOutlined /> 编辑
          </>
        ) : (
          <>
            <SearchOutlined /> 查看
          </>
        )}
      </Typography.Link>
    ),
  }
  /** 加载表格数据 */
  const loadMainData = useCallback(
    (pageIndex: number, order: number, isDownLoad = false, getTotalCallBack?: (num: number) => void) => {
      setLoading(true)
      const {taskTime, taskStatus, ...other} = form.getFieldsValue()
      const beginDate = taskTime ? taskTime[0]?.format('YYYY-MM-DD') : undefined
      const endDate = taskTime ? taskTime[1]?.format('YYYY-MM-DD') : undefined
      const params = {
        pageIndex: isDownLoad ? 1 : pageIndex,
        pageSize: isDownLoad ? 10000000 : getTotalCallBack ? 0 : pageSize,
        beginDate,
        endDate,
        taskStatus: getTotalCallBack ? 0 : taskStatus,
        ...other,
        order,
        taskInputType: screenType,
      }
      AxiosUtil.AxiosRequest('/api/digitalizeTask/list', params, (suc, dat) => {
        if (suc) {
          if (isDownLoad) {
            const list = dat.list.map((item: any) => ({
              ...item,
              symbol: `'${item.symbol}`,
              digitalSign: item.digitalSign ? '有' : '无',
              taskType: mapData.taskTypeMap[item.taskType],
              taskDay: item.taskDay ? moment(item.taskDay).format('YYYY-MM-DD') : '',
              finishDay: item.finishDay ? moment(item.finishDay).format('YYYY-MM-DD') : '',
              taskStatus: mapData.industryMap[item.taskStatus],
            }))
            setCsvData(list)
            document.getElementById(csvLinkId)?.click()
          } else {
            if (getTotalCallBack) getTotalCallBack(dat.total)
            else setTableData(dat)
          }
        }
        setLoading(false)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, screenType]
  )
  /** 点击查询的回调 */
  const onSearch = () => loadMainData(pageIndex, order)
  /** 排序发生变化的回调 */
  const onSortChange: TableProps<any>['onChange'] = (_, __, sorter: any) => {
    if (sorter.field && sorter.order) setOrder(orderMap[sorter.field + sorter.order])
    else setOrder(0)
  }
  /** 通过 ref 向外暴露的方法 */
  const loadData = () => {
    if (pageIndex !== 1) setPageIndex(1)
    else loadMainData(pageIndex, order)
  }
  const loadTotal = (callBack?: (num: number) => void) => loadMainData(1, order, false, callBack)

  useEffect(() => AxiosUtil.AxiosRequest('/api/digitalizeTask/taskSelectList', {}, (suc, dat: IMapData) => suc && setMapData(dat)), [])
  useEffect(() => {
    if (screenType === undefined) {
      AxiosUtil.AxiosRequest('/api/user/getUser', {pageSize: 100, role_id: 27, rtype: 2}, (suc, dat) => {
        if (suc) {
          if (dat.total) {
            setUserList(dat.list.map((item: any) => ({value: item.user_id, label: item.user_name})))
            onUserListChange && onUserListChange(dat.list)
          } else {
            setUserList(defaultUserList)
            onUserListChange && onUserListChange(defaultUserList)
          }
        }
      })
    }
  }, [defaultUserList, onUserListChange, screenType])
  useEffect(() => loadMainData(pageIndex, order), [loadMainData, pageIndex, order])
  useImperativeHandle(onRef, () => ({loadData, setLoading, selectedRowKeys, setSelectedRowKeys, loadTotal, form}))

  return (
    <Spin spinning={loading}>
      <Form form={form} onFinish={onSearch} labelCol={{span: 4}} wrapperCol={{span: 19}}>
        <Row gutter={[32, 0]}>
          <Col span={7}>
            <Form.Item style={formItemStyle} name={'taskTime'} label={'日期'} initialValue={[moment().subtract(7, 'days'), moment()]}>
              <DatePicker.RangePicker style={{width: '100%'}} allowClear format={'YYYY-MM-DD'} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              labelCol={{span: 6}}
              wrapperCol={{span: 18}}
              style={formItemStyle}
              name={'digitalSign'}
              label={'标识'}
              initialValue={screenType === undefined ? 1 : undefined}
            >
              <Select allowClear placeholder={'任务是否有标识!'} options={digitalSign} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              labelCol={{span: 6}}
              wrapperCol={{span: 18}}
              style={formItemStyle}
              name={'taskType'}
              label={'类型'}
              initialValue={screenType === undefined ? '1' : screenType === 1 ? undefined : '0'}
            >
              <Select
                allowClear={[undefined, 1].includes(screenType)}
                options={Object.entries(mapData.taskTypeMap)
                  .map((item) => ({value: item[0], label: item[1]}))
                  .filter((item) => {
                    if (screenType === 0) return item.value === '0'
                    else if (screenType === 1) return item.value !== '0'
                    else return true
                  })}
                placeholder={'请选择任务类型!'}
              />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item style={formItemStyle} name={'searchText'} label={'股票'} initialValue={''}>
              <Input allowClear placeholder={'请输入搜索的内容!'} autoComplete={'new-password'} />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item style={formItemStyle} name={'sfcIndustry1Code'} label={'行业'}>
              <Select
                allowClear
                options={Object.entries(mapData.industryMap).map((item) => ({value: item[0], label: item[1]}))}
                placeholder={'请选择行业!'}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              labelCol={{span: 6}}
              wrapperCol={{span: 18}}
              style={formItemStyle}
              name={'taskStatus'}
              label={'状态'}
              initialValue={screenType === undefined ? '0' : '1'}
            >
              <Select
                allowClear
                options={Object.entries(mapData.taskMap).map((item) => ({value: item[0], label: item[1]}))}
                placeholder={'请选择任务状态!'}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              labelCol={{span: 6}}
              wrapperCol={{span: 18}}
              style={formItemStyle}
              name={'userId'}
              label={'分配'}
              initialValue={screenType === undefined ? undefined : loginInfo.user_id || undefined}
            >
              <Select allowClear={screenType === undefined} placeholder={'请选择分配的人员!'} options={userList} />
            </Form.Item>
          </Col>
          <Col span={7} style={{}}>
            <Form.Item style={formItemStyle} wrapperCol={{span: 24}}>
              <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <Button type="primary" htmlType={'submit'}>
                  <SearchOutlined /> 查询
                </Button>
                <Button type={'link'} style={{margin: '0 16px 0'}} onClick={() => loadMainData(pageIndex, order, true)}>
                  <CloudDownloadOutlined /> 下载
                </Button>
                <CSVLink id={csvLinkId} filename={csvName} headers={csvHeader} data={csvData} style={{display: 'none'}}>
                  <CloudDownloadOutlined /> 下载
                </CSVLink>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        rowKey={'id'}
        bordered
        size={'small'}
        columns={onOperate ? [...columns, operateCol] : columns}
        dataSource={tableData.list}
        onChange={onSortChange}
        scroll={{scrollToFirstRowOnChange: true}}
        rowSelection={onOperate ? undefined : {selectedRowKeys, onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys)}}
        pagination={{pageSize, current: pageIndex, onChange: setPageIndex, total: tableData.total, size: 'default'}}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{fontWeight: 500}}>
              {onOperate ? <></> : <Table.Summary.Cell index={0} align={'center'} colSpan={1} />}
              <Table.Summary.Cell index={1} align={'center'} colSpan={1}>
                合计
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align={'center'} colSpan={1}>
                {tableData.total}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align={'center'} colSpan={onOperate ? 10 : 9} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Spin>
  )
}
